var __awaiter = (this && this.__awaiter) || function (thisArg, _arguments, P, generator) {
    function adopt(value) { return value instanceof P ? value : new P(function (resolve) { resolve(value); }); }
    return new (P || (P = Promise))(function (resolve, reject) {
        function fulfilled(value) { try { step(generator.next(value)); } catch (e) { reject(e); } }
        function rejected(value) { try { step(generator["throw"](value)); } catch (e) { reject(e); } }
        function step(result) { result.done ? resolve(result.value) : adopt(result.value).then(fulfilled, rejected); }
        step((generator = generator.apply(thisArg, _arguments || [])).next());
    });
};
import { createElement as _createElement } from "react";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Autocomplete, Box, Stack, TextField } from "@mui/material";
import { useForm, Controller } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { createFilterOptions } from "@mui/material/Autocomplete";
import CustomModal from "../modal/CustomModal";
import ContactSearchApi from "../../../infrastructure/api/pages/contact-search-api";
// Standalone add/edit for the Contacts address book. Deliberately posts to the
// permission-gated contacts-search endpoints (Permission.Contacts.Create / .Manage),
// NOT the lead-scoped lead-contacts endpoints — so creating/editing from this screen
// is actually enforced server-side. Adding requires selecting a lead to link to
// (the backend create-or-link is lead-scoped); editing updates the contact's own
// fields globally and never touches its other lead links.
const ContactFormModal = ({ open, mode, contact, onClose, onSaved, }) => {
    const { t: translate } = useTranslation();
    const [leadOptions, setLeadOptions] = useState([]);
    const [selectedLead, setSelectedLead] = useState(null);
    const [loadingLeads, setLoadingLeads] = useState(false);
    const [saving, setSaving] = useState(false);
    const schema = yup.object({
        name: yup.string().trim().required(translate("NameRequired")),
        function: yup.string(),
        email: yup.string().email(translate("InvalidEmail")),
        phoneNumber: yup.string(),
    });
    const { control, handleSubmit, reset, formState: { errors }, } = useForm({
        resolver: yupResolver(schema),
        defaultValues: { name: "", function: "", email: "", phoneNumber: "" },
    });
    // Only fetch the (large) leads list when adding — editing needs no lead picker.
    useEffect(() => {
        var _a, _b, _c, _d;
        if (!open)
            return;
        setSelectedLead(null);
        reset({
            name: (_a = contact === null || contact === void 0 ? void 0 : contact.name) !== null && _a !== void 0 ? _a : "",
            function: (_b = contact === null || contact === void 0 ? void 0 : contact.function) !== null && _b !== void 0 ? _b : "",
            email: (_c = contact === null || contact === void 0 ? void 0 : contact.email) !== null && _c !== void 0 ? _c : "",
            phoneNumber: (_d = contact === null || contact === void 0 ? void 0 : contact.phoneNumber) !== null && _d !== void 0 ? _d : "",
        });
        if (mode === "add") {
            setLoadingLeads(true);
            ContactSearchApi.getLeadsDropdown()
                .then((res) => { var _a; return setLeadOptions((_a = res.data) !== null && _a !== void 0 ? _a : []); })
                .catch(() => toast.error(translate("Error while loading leads.")))
                .finally(() => setLoadingLeads(false));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [open, mode, contact]);
    const onSubmit = (values) => __awaiter(void 0, void 0, void 0, function* () {
        var _a, _b, _c, _d, _e, _f, _g, _h;
        if (mode === "add" && !selectedLead) {
            toast.error(translate("Please select a lead to link the contact to."));
            return;
        }
        setSaving(true);
        try {
            if (mode === "add") {
                yield ContactSearchApi.create({
                    name: values.name.trim(),
                    function: (_a = values.function) === null || _a === void 0 ? void 0 : _a.trim(),
                    email: (_b = values.email) === null || _b === void 0 ? void 0 : _b.trim(),
                    phoneNumber: (_c = values.phoneNumber) === null || _c === void 0 ? void 0 : _c.trim(),
                    leadId: selectedLead.value,
                });
                toast.success(translate("Contact created successfully."));
            }
            else {
                yield ContactSearchApi.update({
                    id: contact.id,
                    name: values.name.trim(),
                    function: (_d = values.function) === null || _d === void 0 ? void 0 : _d.trim(),
                    email: (_e = values.email) === null || _e === void 0 ? void 0 : _e.trim(),
                    phoneNumber: (_f = values.phoneNumber) === null || _f === void 0 ? void 0 : _f.trim(),
                });
                toast.success(translate("Contact updated successfully."));
            }
            onSaved();
            onClose();
        }
        catch (error) {
            toast.error(((_h = (_g = error === null || error === void 0 ? void 0 : error.response) === null || _g === void 0 ? void 0 : _g.data) === null || _h === void 0 ? void 0 : _h.error) || translate("Error while saving contact."));
        }
        finally {
            setSaving(false);
        }
    });
    // Cap rendered option nodes so the (potentially thousands-long) leads list never
    // stalls the dialog — only the first 50 matches mount as the user types.
    const filterLeadOptions = createFilterOptions({ limit: 50 });
    return (_jsx(CustomModal, { open: open, onClose: () => {
            if (!saving)
                onClose();
        }, size: "xs", saving: saving, handleSaveButtonClick: handleSubmit(onSubmit), heading: mode === "add" ? translate("Add contact") : translate("Edit contact"), saveButtonText: mode === "add" ? translate("Add") : translate("Save"), disabled: saving, children: _jsxs(Stack, { spacing: 2.25, sx: { pt: 2, pb: 1 }, children: [_jsx(Controller, { name: "name", control: control, render: ({ field }) => {
                        var _a;
                        return (_jsx(TextField, Object.assign({}, field, { label: translate("Name"), fullWidth: true, error: !!errors.name, helperText: (_a = errors.name) === null || _a === void 0 ? void 0 : _a.message, slotProps: {
                                htmlInput: { "data-testid": "cf-name" },
                                inputLabel: { shrink: true }
                            } })));
                    } }), _jsx(Controller, { name: "function", control: control, render: ({ field }) => (_jsx(TextField, Object.assign({}, field, { label: translate("Job title"), fullWidth: true, slotProps: {
                            htmlInput: { "data-testid": "cf-function" },
                            inputLabel: { shrink: true }
                        } }))) }), _jsx(Controller, { name: "email", control: control, render: ({ field }) => {
                        var _a;
                        return (_jsx(TextField, Object.assign({}, field, { label: translate("Email address"), fullWidth: true, error: !!errors.email, helperText: (_a = errors.email) === null || _a === void 0 ? void 0 : _a.message, slotProps: {
                                htmlInput: { "data-testid": "cf-email" },
                                inputLabel: { shrink: true }
                            } })));
                    } }), _jsx(Controller, { name: "phoneNumber", control: control, render: ({ field }) => (_jsx(TextField, Object.assign({}, field, { label: translate("Phone number"), fullWidth: true, slotProps: {
                            htmlInput: { "data-testid": "cf-phone" },
                            inputLabel: { shrink: true }
                        } }))) }), mode === "add" && (_jsx(Autocomplete, { options: leadOptions, getOptionLabel: (o) => o.text, loading: loadingLeads, value: selectedLead, onChange: (_, value) => setSelectedLead(value), isOptionEqualToValue: (o, v) => o.value === v.value, filterOptions: filterLeadOptions, renderOption: (optionProps, option) => (_createElement(Box, Object.assign({ component: "li" }, optionProps, { key: option.value, sx: { minWidth: 0 } }), option.text)), renderInput: (params) => (_jsx(TextField, Object.assign({}, params, { label: translate("Link to lead"), fullWidth: true, required: true, slotProps: Object.assign(Object.assign({}, params.slotProps), { htmlInput: Object.assign(Object.assign({}, params.slotProps.htmlInput), { "data-testid": "cf-lead" }), inputLabel: Object.assign(Object.assign({}, params.slotProps.inputLabel), { shrink: true }) }) }))) }))] }) }));
};
export default ContactFormModal;
